import { useState, type FormEvent } from 'react'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { api, ApiError } from '../../lib/api'
import { formatCents, parseEurosToCents } from '../../lib/money'
import { PaymentMethodLabel, type PaymentMethodT } from '@jr2os/shared'

interface Props {
  invoiceId: string
  pendingCents: number
  onClose: () => void
  onSaved: () => void | Promise<void>
}

export default function RegisterPaymentModal({
  invoiceId,
  pendingCents,
  onClose,
  onSaved,
}: Props) {
  const { t } = useTranslation(['billing', 'common'])
  const [paidAt, setPaidAt] = useState(new Date().toISOString().slice(0, 10))
  const [amount, setAmount] = useState((pendingCents / 100).toFixed(2).replace('.', ','))
  const [method, setMethod] = useState<PaymentMethodT>('TRANSFERENCIA')
  const [reference, setReference] = useState('')
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    const cents = parseEurosToCents(amount)
    if (cents <= 0) {
      setError(t('registerPayment.invalidAmount'))
      return
    }
    setBusy(true)
    setError(null)
    try {
      await api.post(`/invoices/${invoiceId}/payments`, {
        paidAt: new Date(paidAt).toISOString(),
        amountCents: cents,
        method,
        reference: reference || null,
        notes: notes || null,
      })
      await onSaved()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : t('registerPayment.genericError')))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center px-4"
      onClick={onClose}
    >
      <form
        onSubmit={onSubmit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md surface-deep p-5 animate-slideUp space-y-3"
      >
        <h3 className="text-base font-semibold t-primary">{t('registerPayment.title')}</h3>
        <p className="text-xs t-muted">{t('registerPayment.pending')}: {formatCents(pendingCents)}</p>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs t-muted">{t('registerPayment.date')}</span>
            <input
              type="date"
              value={paidAt}
              onChange={(e) => setPaidAt(e.target.value)}
              className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary mt-0.5"
              required
            />
          </label>
          <label className="block">
            <span className="text-xs t-muted">{t('registerPayment.amount')}</span>
            <input
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0,00"
              className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary mt-0.5 font-mono"
              required
            />
          </label>
        </div>

        <label className="block">
          <span className="text-xs t-muted">{t('registerPayment.method')}</span>
          <select
            value={method}
            onChange={(e) => setMethod(e.target.value as PaymentMethodT)}
            className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary mt-0.5"
          >
            {Object.entries(PaymentMethodLabel).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="text-xs t-muted">{t('registerPayment.referenceOptional')}</span>
          <input
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder={t('registerPayment.referencePlaceholder')}
            className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary mt-0.5"
          />
        </label>

        <label className="block">
          <span className="text-xs t-muted">{t('registerPayment.notesOptional')}</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary mt-0.5"
          />
        </label>

        {error && <p className="text-xs text-rose-700 dark:text-rose-300">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover"
          >
            {t('common:actions.cancel')}
          </button>
          <button
            type="submit"
            disabled={busy}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 disabled:opacity-50 flex items-center gap-1.5"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            {t('registerPayment.submit')}
          </button>
        </div>
      </form>
    </div>
  )
}
